import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from "react";
import { useTheme } from "@mui/material/styles";

import MDSnackbar from "components/MDSnackbar";

import { getUnReadNotifications, type MessageNotification } from "../../../shared/api/notification";
import { useSessionContext } from "context/SessionContext";

export interface MessageNotificationContextProps {
  messages: MessageNotification[];
  unreadCount: number;
  addMessage: (message: MessageNotification) => void;
  markAsRead: (id: number) => void;
  markAllAsRead: () => void;
  removeMessage: (id: number) => void;
}

const MessageNotificationContext = createContext<MessageNotificationContextProps>({
  messages: [],
  unreadCount: 0,
  addMessage: () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  removeMessage: () => {},
});

export const MessageNotificationProvider = ({ children }: { children: ReactNode }) => {
  const [messages, setMessages] = useState<MessageNotification[]>([]);
  const [snackbars, setSnackbars] = useState<MessageNotification[]>([]);
  const timers = useRef<Record<string, NodeJS.Timeout>>({});
  
  const { accessToken, socket } = useSessionContext();

  const theme = useTheme();

  const removeSnackbar = useCallback((id: number) => {
    setSnackbars((prev) => prev.filter((s) => s.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showSnackbar = useCallback((msg: MessageNotification) => {
    setSnackbars((prev) => {
      const updated = [msg, ...prev];
      return updated.slice(0, 5);
    });

    timers.current[msg.id] = setTimeout(() => {
      removeSnackbar(msg.id);
    }, 4000);
  }, [removeSnackbar]);

  const addMessage = useCallback((message: MessageNotification) => {
    setMessages((prev) => {
      const exists = prev.some((m) => m.id === message.id);
      if (exists) return prev;
      return [message, ...prev.slice(0, 9)];
    });
    showSnackbar(message);
  }, [showSnackbar]);

  const removeMessage = (id: number) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const markAsRead = (id: number) => {
    setMessages((prev) => prev.map((msg) => (msg.id === id ? { ...msg, read: true } : msg)));
  };

  const markAllAsRead = () => {
    setMessages((prev) => prev.map((msg) => ({ ...msg, read: true })));
  };

  const unreadCount = messages.filter((m) => !m.read).length;
  
  useEffect(() => {
    if (!accessToken || !socket) {
      return;
    }

    const handler = (data: MessageNotification) => {
      console.log("📥 message_notification 受信:", data);
      addMessage(data);
    };

    socket.on("message_notification", handler);
    console.log("📡 message_notification リスナー登録済み");

    const fetchUnread = async () => {
      try {
        const unread = await getUnReadNotifications(accessToken);
        console.log("APIから未読通知取得:", unread);

        const transformed: MessageNotification[] = unread.map((msg) => ({
          id: msg.id,
          title: msg.title,
          content: msg.content,
          datetime: msg.datetime,
          read: msg.read,
        }));

        setMessages(transformed);
      } catch (error) {
        console.error("未読通知の取得に失敗:", error);        
      }
    };

    fetchUnread();
    
    return () => {
      socket.off("message_notification", handler);
      console.log("📡 message_notification リスナー解除");
    };
  }, [accessToken, socket, addMessage]);

  return (
    <MessageNotificationContext.Provider
      value={{ messages, unreadCount, addMessage, markAsRead, markAllAsRead, removeMessage }}
    >
      {children}
      {theme &&
        [...snackbars].reverse().map((notification, index) => {
          return (
            <MDSnackbar
              key={notification.id}
              icon="info"
              title={notification.title}
              content={notification.content}
              color="info"
              dateTime=""
              open={true}
              onClose={() => removeSnackbar(notification.id)}
              close={() => removeSnackbar(notification.id)}
              sx={{
                position: "fixed",
                bottom: `${24 + index * 75}px`,
                right: { xs: "50%", sm: "50%", md: "24px" },
                transform: { xs: "translateX(50%)", sm: "translateX(50%)", md: "none" },
                zIndex: 9999,
                width: { xs: "calc(100% - 32px)", sm: "calc(100% - 32px)", md: "auto" },
                maxWidth: 360,
              }}
            />
          );
        })}
    </MessageNotificationContext.Provider>
  );
};

export const useMessageNotification = () => useContext(MessageNotificationContext);
